import { useState } from 'react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import logo from '@/assets/img/logo.svg';
import { AreasActuation } from '@/components/CardActuationArea';
import ModalCnpjCadastre from '@/components/Modals/Cadastre/Cnpj';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { MyErrorResponse, handleErrorAsync } from '@/utils/handlers';
import { cnpjUnmask } from '@/utils/masks';
import { hasParamsMarketing } from '@/utils/validates';
import { withSSRGuest } from '@/utils/withSSRGuest';
import { ArrowBack } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { AxiosError } from 'axios';

const RegisterForm = dynamic(() => import('@/components/RegisterPage/Form'), {
  loading: () => (
    <CircularProgress
      size={80}
      sx={{
        position: 'absolute',
        top: 'calc(50% - 40px)',
        left: 'calc(50% - 40px)',
      }}
    />
  ),
});

const ModalActuationArea = dynamic(
  () => import('@/components/RegisterPage/ModalActuationArea'),
  {
    loading: () => (
      <CircularProgress
        size={80}
        sx={{
          position: 'absolute',
          top: 'calc(50% - 40px)',
          left: 'calc(50% - 40px)',
        }}
      />
    ),
  }
);

interface IRegisterPageProps {
  initialName: string;
  initialEmail: string | null;
  initialPhone: string;
  initialCnpj: string;
  initialCpf: string;
}

interface ICreateUseParams {
  name: string;
  username: string;
  email: string;
  phone?: string;
  cpf: string;
  signup_cnpj?: string;
  re_password: string;
  password: string;
  is_supplier?: boolean;
  is_candidate?: boolean;
  is_service_provider?: boolean;
  is_constructor?: boolean;
  is_customer?: boolean;
  is_obraprima_customer?: boolean;
}

type Views = 'CNPJ_FORM' | 'USER_DATA_FORM' | 'ACTUATION_AREA' | 'CLOSED';

const RegisterPage = ({
  initialCnpj,
  initialCpf,
  initialEmail,
  initialName,
  initialPhone,
}: IRegisterPageProps) => {
  const router = useRouter();
  const { signIn } = useUser();
  const { setIsLoading } = useLoading();
  const { aid, chave } = router.query;

  const [formUserData, setFormUserData] = useState({
    email: initialEmail || '',
    phone: initialPhone || '',
    name: initialName || '',
    cpf: initialCpf || '',
    password: '',
    re_password: '',
    agreePrivacyPolicy: false,
  });
  const [cnpjInformedModal, setCnpjInformedModal] = useState('');

  const [view, setView] = useState<Views>(
    hasParamsMarketing(router) || router.query.cnpj === 'modal_true'
      ? 'CNPJ_FORM'
      : 'USER_DATA_FORM'
  );

  const isActuationArea = view === 'ACTUATION_AREA';

  const isQuotationAnswerUser =
    router.query.cnpj === 'modal_true' && aid?.toString() && chave?.toString();
  const isMarketingUser = !isQuotationAnswerUser && hasParamsMarketing(router);

  const subscribeInTheCodeFeatureFlag = async ({
    codeFeatureFlag,
    email,
  }: {
    codeFeatureFlag?: AreasActuation;
    email: string;
  }) => {
    await api.post(`/api/feature_flags/${codeFeatureFlag}/subscriptions/`, {
      email,
    });
  };

  const createUser = async (userData: ICreateUseParams) => {
    try {
      const firstName = userData.name.split(/\s/)[0];

      await api.post('/auth/users/', {
        ...userData,
        signup_cnpj: userData.signup_cnpj
          ? cnpjUnmask(userData.signup_cnpj)
          : userData.signup_cnpj,
        display_name: firstName,
      });
    } catch (e) {
      const error = e as AxiosError<MyErrorResponse>;
      const errorData = error?.response?.data;
      if (errorData) {
        if (errorData.detail) {
          throw Error(errorData.detail);
        }
        if (errorData.name) {
          throw Error(`Nome: ${errorData.name}`);
        }
        if (errorData.cpf) {
          throw Error(`Cpf: ${errorData.cpf}`);
        }
        if (errorData.email) {
          throw Error(`Email: ${errorData.email}`);
        }
        if (errorData.phone) {
          throw Error(`Telefone: ${errorData.phone}`);
        }
        if (errorData.cnpj) {
          throw Error(`Cnpj: ${errorData.cnpj}`);
        }
        if (errorData.password) {
          throw Error(`Senha: ${errorData.password}`);
        }
        if (errorData.re_password) {
          throw Error(`Confirmação de senha: ${errorData.re_password}`);
        }

        throw error;
      }

      throw Error('Houve um erro, tente novamente mais tarde');
    }
  };

  const handleSubmitNewCnpj = async (cnpj: string) => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await createUser({
        ...formUserData,
        username: formUserData.email,
        signup_cnpj: cnpj,
        is_supplier: true,
      });
      return;
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    handleCloseConcludedSignUp(formUserData.email, formUserData.password);
  };

  const handleCloseConcludedSignUp = async (
    username: string,
    password: string
  ) => {
    const signInOptions = {
      preventRedirect: true,
      successCallback: () => {
        if (aid && chave && !isQuotationAnswerUser) {
          router.push({
            pathname: `/resposta-cotacoes/${aid}`,
            query: { chave },
          });
        } else {
          router.push({
            pathname: '/home',
            query: {
              verified_email: true,
            },
          });
          Toast(
            'success',
            'Cadastro concluído com sucesso! Aguarde enquanto te redirecionamos'
          );
        }
      },
    };

    setIsLoading(true);
    await signIn(
      {
        username,
        password,
      },
      signInOptions
    );
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        height: 'calc(100% - 74px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          backgroundColor: 'primary.main',
          width: '100%',
          height: '74px',
          justifyContent: 'center',
        }}
      >
        <Link href="/">
          <Image
            height="10px"
            width="220px"
            quality={100}
            src={logo}
            alt="Logo"
            style={{ cursor: 'pointer' }}
          />
        </Link>
      </Box>
      <Box
        component="main"
        sx={theme => ({
          backgroundColor: 'primary.main',
          display: 'grid',
          placeItems: 'center',
          height: 'auto',
          minHeight: '100%',
          [theme.breakpoints.up('sm')]: {
            backgroundColor: 'primary.contrastText',
          },
        })}
      >
        {!isActuationArea && (
          <Box
            boxShadow="0px 1px 1px rgba(0, 0, 0, 0.1)"
            position="relative"
            bgcolor="background.paper"
            sx={theme => ({
              width: '100%',
              borderRadius: '10px 10px 0 0',
              [theme.breakpoints.up('sm')]: {
                borderRadius: '0px',
              },
            })}
          >
            <MainContainer
              sx={{
                position: 'relative',
                padding: '18px 16px',
                textAlign: 'center',
              }}
            >
              <IconButton
                onClick={() => {
                  router.push('/');
                }}
                sx={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <ArrowBack
                  sx={{
                    color: 'text.primary',
                  }}
                />
              </IconButton>
              <Typography component="h3" variant="h6">
                Cadastro
              </Typography>
            </MainContainer>
          </Box>
        )}
        <Box
          sx={theme => ({
            borderRadius: isActuationArea ? '10px 10px 0 0' : '',
            backgroundColor: 'primary.contrastText',
            height: '100%',
            minHeight: isActuationArea ? '600px' : '640px',
            width: '100%',
            padding: '28px 16px 38px 16px',
            margin: '38px auto',
            boxShadow: 'none',
            position: 'relative',
            zIndex: '2',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            [theme.breakpoints.up('sm')]: {
              borderRadius: '10px',
              height: 'auto',
              width: '520px',
              padding: '38px',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            },
          })}
        >
          {
            {
              RegisterForm: (
                <RegisterForm
                  initialValues={{
                    email: formUserData.email,
                    phone: formUserData.phone,
                    name: formUserData.name,
                    cpf: formUserData.cpf,
                  }}
                  onSubmit={async values => {
                    setFormUserData(values);
                    if (!isQuotationAnswerUser && !isMarketingUser) {
                      setView('ACTUATION_AREA');
                      return;
                    }

                    await createUser({
                      ...values,
                      username: values.email,
                      signup_cnpj: cnpjInformedModal,
                      is_supplier: true,
                    });
                    handleCloseConcludedSignUp(values.email, values.password);
                  }}
                />
              ),
              ModalActuationArea: (
                <ModalActuationArea
                  onClose={() => setView('USER_DATA_FORM')}
                  nextStep={() => setView('CNPJ_FORM')}
                  createUser={areaActuationCardSelected => {
                    return createUser({
                      ...formUserData,
                      username: formUserData.email,
                      signup_cnpj: initialCnpj,
                      is_supplier:
                        areaActuationCardSelected === AreasActuation.SUPPLIER,
                      is_candidate:
                        areaActuationCardSelected === AreasActuation.CANDIDATE,
                      is_constructor:
                        areaActuationCardSelected ===
                        AreasActuation.CONSTRUCTOR,
                      is_customer:
                        areaActuationCardSelected === AreasActuation.CUSTOMER,
                      is_service_provider:
                        areaActuationCardSelected ===
                        AreasActuation.SERVICE_PROVIDER,
                      is_obraprima_customer:
                        areaActuationCardSelected ===
                        AreasActuation.OBRAPRIMA_CUSTOMER,
                    });
                  }}
                  subscribeInTheCodeFeatureFlag={areaActuationCardSelected => {
                    return subscribeInTheCodeFeatureFlag({
                      email: formUserData.email,
                      codeFeatureFlag: areaActuationCardSelected,
                    });
                  }}
                />
              ),
            }[isActuationArea ? 'ModalActuationArea' : 'RegisterForm']
          }
        </Box>
      </Box>
      <ModalCnpjCadastre
        closeModal={
          !isQuotationAnswerUser && !isMarketingUser
            ? () => setView('ACTUATION_AREA')
            : undefined
        }
        isOpen={view === 'CNPJ_FORM'}
        handleSubmitNewCnpj={async cnpj => {
          setCnpjInformedModal(cnpj);
          if (!isQuotationAnswerUser && !isMarketingUser) {
            await handleSubmitNewCnpj(cnpj);
            return;
          }
          setView('USER_DATA_FORM');
        }}
        textButton={
          !isQuotationAnswerUser && !isMarketingUser ? 'Cadastrar' : 'Próximo'
        }
      />
    </Box>
  );
};

export default RegisterPage;

export const getServerSideProps: GetServerSideProps = withSSRGuest(
  async context => {
    const { aid, chave } = context.query;
    let initialName = '';
    let initialCpf = '';
    let initialCnpj = '';
    let initialPhone = '';
    let initialEmail = '';
    if (aid && chave) {
      try {
        const response = await api.get(`/api/quotation_answers/${aid}/`, {
          params: { key: chave },
        });
        if (response.status === 200) {
          initialName = response.data.name;
          initialCpf = response.data.cpf;
          initialCnpj = response.data.cnpj;
          initialPhone = response.data.phone;
          initialEmail = response.data.email;
        }
      } catch (e) {
        console.log(e);
      }
    }
    return {
      props: {
        initialName: initialName,
        initialEmail: initialEmail,
        initialCnpj: initialCnpj,
        initialPhone: initialPhone,
        initialCpf: initialCpf,
      },
    };
  }
);
